import React, { useState, useEffect } from 'react'
import { format, parse, isValid } from 'date-fns'
import { useTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import Label from '@/v2/components/common/FieldLabel'
import { Wrapper, ErrorText, HelpText } from './styles'

const DateOfBirthInput = ({
  label = 'Date of birth',
  value,
  error,
  disabled = false,
  onChange,
  placeholder = 'MM/DD/YYYY',
  helpText = 'Enter date as MM/DD/YYYY',
  required = false,
  maxDate = new Date(),
  minDate = new Date(1900, 0, 1),
  outlineColor,
}) => {
  const theme = useTheme()
  const [dateValue, setDateValue] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [validationError, setValidationError] = useState('')

  useEffect(() => {
    if (value) {
      if (typeof value === 'string') {
        if (value.includes('/') && value.length === 10) {
          const parsedDate = parse(value, 'MM/dd/yyyy', new Date())
          if (parsedDate && isValid(parsedDate)) {
            setDateValue(parsedDate)
            setInputValue(value)
          } else {
            setDateValue(null)
            setInputValue(value)
          }
        } else if (value.includes('-')) {
          const parsedDate = parse(value, 'yyyy-MM-dd', new Date())
          if (parsedDate && isValid(parsedDate)) {
            setDateValue(parsedDate)
            setInputValue(format(parsedDate, 'MM/dd/yyyy'))
          } else {
            setDateValue(null)
            setInputValue('')
          }
        } else {
          setDateValue(null)
          setInputValue(value)
        }
      } else if (value instanceof Date && isValid(value)) {
        setDateValue(value)
        setInputValue(format(value, 'MM/dd/yyyy'))
      } else {
        setDateValue(null)
        setInputValue('')
      }
    } else {
      setDateValue(null)
      setInputValue('')
    }
  }, [value])

  const handleDateChange = newValue => {
    if (newValue && isValid(newValue)) {
      setDateValue(newValue)
      setInputValue(format(newValue, 'MM/dd/yyyy'))
      onChange && onChange(newValue)
      setValidationError('')
    } else if (newValue === null) {
      setDateValue(null)
      setInputValue('')
      onChange && onChange(null)
      setValidationError('')
    }
  }

  const handleInputChange = (_, newInputValue) => {
    setInputValue(newInputValue)

    if (!newInputValue) {
      setDateValue(null)
      onChange && onChange(null)
      setValidationError('')
      return
    }

    const digitsOnly = newInputValue.replace(/[^\d]/g, '')

    if (digitsOnly.length <= 8) {
      let formatted = ''
      for (let i = 0; i < digitsOnly.length; i++) {
        if (i === 2 || i === 4) {
          formatted += '/'
        }
        formatted += digitsOnly[i]
      }
      setInputValue(formatted)
    }

    if (newInputValue.length === 10) {
      const parsedDate = parse(newInputValue, 'MM/dd/yyyy', new Date())
      if (isValid(parsedDate)) {
        setDateValue(parsedDate)
        onChange && onChange(parsedDate)
        setValidationError('')
      } else {
        setDateValue(null)
        onChange && onChange(null)
        setValidationError('Invalid date')
      }
    } else {
      setDateValue(null)
      onChange && onChange(null)
    }
  }

  const displayError = validationError || error

  return (
    <Wrapper>
      {label && (
        <Label style={{ marginBottom: '4px' }}>
          {label}
          {required && (
            <span style={{ color: theme.palette.error.main }}> *</span>
          )}
        </Label>
      )}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          value={dateValue}
          onChange={handleDateChange}
          onInputChange={handleInputChange}
          inputValue={inputValue}
          disabled={disabled}
          maxDate={maxDate}
          minDate={minDate}
          inputFormat="MM/dd/yyyy"
          mask="__/__/____"
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              size="small"
              placeholder={placeholder}
              error={!!displayError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor:
                      outlineColor || theme.palette.grey?.[400] || '#ccc',
                  },
                  '&:hover fieldset': {
                    borderColor:
                      outlineColor || theme.palette.grey?.[600] || '#999',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor:
                      outlineColor || theme.palette.primary?.main || '#0074FE',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.grey?.[100] || '#f5f5f5',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px',
                  fontSize: '16px',
                  '@media (min-width: 1025px)': {
                    fontSize: '14px',
                  },
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
      {!displayError && helpText && !inputValue && (
        <HelpText theme={theme.palette}>{helpText}</HelpText>
      )}
      {displayError && (
        <ErrorText theme={theme.palette}>{displayError}</ErrorText>
      )}
    </Wrapper>
  )
}

export default DateOfBirthInput
